import {
  EPdfBlockType,
  type IPdfBlock,
  type IPdfSection,
} from '@mahawi/eshop-common/dist/src/types';

import {
  type ILegalVersionView,
  type ISeller,
} from '../react/reducers/legal/types';

export const FALLBACK_LANGUAGE = 'en';

export interface ILegalBlock extends IPdfBlock {
  textWithoutPhone?: string;
}

export interface ITermsSection {
  id: string;
  heading: string;
  blocks: ILegalBlock[];
}

export interface IWithdrawalFormContent {
  heading: string;
  intro: string;
  addressee: string;
  statement: string;
  fields: string[];
  footnote: string;
}

export interface ITermsContent {
  title: string;
  subtitle: string;
  versionLabel: string;
  orderDocumentsTitle: string;
  sections: ITermsSection[];
  withdrawalForm: IWithdrawalFormContent;
}

export interface ILegalResourceReader {
  getResource: (
    languageCode: string,
    namespace: string,
    key: string,
  ) => ITermsContent | undefined;
}

export type TInterpolationValues = Record<string, string>;

const isTermsContent = (
  value: ITermsContent | undefined,
): value is ITermsContent =>
  Boolean(
    value &&
    Array.isArray(value.sections) &&
    value.sections.length > 0 &&
    value.withdrawalForm &&
    Array.isArray(value.withdrawalForm.fields),
  );

export const readTermsContent = (
  reader: ILegalResourceReader,
  languageCode: string,
  namespace: string,
): ITermsContent | null => {
  const localized: ITermsContent | undefined = reader.getResource(
    languageCode,
    namespace,
    'terms',
  );

  if (isTermsContent(localized)) {
    return localized;
  }

  const fallback: ITermsContent | undefined = reader.getResource(
    FALLBACK_LANGUAGE,
    namespace,
    'terms',
  );

  return isTermsContent(fallback) ? fallback : null;
};

export const interpolate = (
  text: string,
  values: TInterpolationValues,
): string =>
  text.replace(
    /\{\{\s*(\w+)\s*\}\}/g,
    (match: string, key: string): string => values[key] ?? match,
  );

export const buildInterpolationValues = (
  seller: ISeller | null,
  version: ILegalVersionView | null,
): TInterpolationValues => ({
  ...(seller ?? {}),
  versionCode: version?.versionCode ?? '',
});

const selectBlockText = (
  block: ILegalBlock,
  values: TInterpolationValues,
): string | undefined =>
  values.phone || block.textWithoutPhone === undefined
    ? block.text
    : block.textWithoutPhone;

export const resolveBlock = (
  block: ILegalBlock,
  values: TInterpolationValues,
): IPdfBlock => {
  const text: string | undefined = selectBlockText(block, values);

  return {
    type: block.type,
    ...(text === undefined ? {} : { text: interpolate(text, values) }),
    ...(block.items === undefined
      ? {}
      : {
          items: block.items.map((item: string): string =>
            interpolate(item, values),
          ),
        }),
    ...(block.label === undefined
      ? {}
      : { label: interpolate(block.label, values) }),
  };
};

export const resolveTermsContent = (
  content: ITermsContent,
  values: TInterpolationValues,
): ITermsContent => ({
  title: interpolate(content.title, values),
  subtitle: interpolate(content.subtitle, values),
  versionLabel: interpolate(content.versionLabel, values),
  orderDocumentsTitle: interpolate(content.orderDocumentsTitle, values),
  sections: content.sections.map((section: ITermsSection): ITermsSection => ({
    id: section.id,
    heading: interpolate(section.heading, values),
    blocks: section.blocks.map((block: ILegalBlock): IPdfBlock =>
      resolveBlock(block, values),
    ),
  })),
  withdrawalForm: {
    heading: interpolate(content.withdrawalForm.heading, values),
    intro: interpolate(content.withdrawalForm.intro, values),
    addressee: interpolate(content.withdrawalForm.addressee, values),
    statement: interpolate(content.withdrawalForm.statement, values),
    fields: content.withdrawalForm.fields.map((field: string): string =>
      interpolate(field, values),
    ),
    footnote: interpolate(content.withdrawalForm.footnote, values),
  },
});

export const toWithdrawalFormSection = (
  form: IWithdrawalFormContent,
): IPdfSection => ({
  heading: form.heading,
  blocks: [
    { type: EPdfBlockType.PARAGRAPH, text: form.intro },
    { type: EPdfBlockType.PARAGRAPH, text: form.addressee },
    { type: EPdfBlockType.PARAGRAPH, text: form.statement },
    ...form.fields.map((label: string): IPdfBlock => ({
      type: EPdfBlockType.FORM_FIELD,
      label,
    })),
    { type: EPdfBlockType.PARAGRAPH, text: form.footnote },
  ],
});

export const toPdfSections = (content: ITermsContent): IPdfSection[] => [
  ...content.sections.map((section: ITermsSection): IPdfSection => ({
    heading: section.heading,
    blocks: section.blocks,
  })),
  toWithdrawalFormSection(content.withdrawalForm),
];
